import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import type { Route } from "next";
import { Menu } from "lucide-react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useDebugOverlay } from "@/lib/debug/overlay-registry";
import { useLayout } from "./context";
import { HeaderBreadcrumbs } from "./header-breadcrumbs";
import { HeaderTitle } from "./header-title";
import { HeaderTitleDefault } from "./header-title-default";
import { Sidebar } from "./sidebar";
import { SidebarMenu } from "./sidebar-menu";

export function HeaderLogo() {
  const t = useTranslations("layout30");
  const { isMobile, settings } = useLayout();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const navigationTitle = t(
    settings.audience === "supplier"
      ? "supplierNavigationTitle"
      : "navigationTitle",
  );
  useDebugOverlay(
    "sheet",
    "components/layouts/layout-30/components/header-logo.tsx",
    isSheetOpen,
    navigationTitle,
  );

  return (
    <div
      data-slot="header-logo"
      data-debug-source="components/layouts/layout-30/components/header-logo.tsx"
      className="flex min-w-0 flex-1 items-center gap-2 sm:gap-2.5"
    >
      <div className="flex shrink-0 items-center gap-1">
        {isMobile ? (
          <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                mode="icon"
                aria-label={t(
                  settings.audience === "supplier"
                    ? "supplierOpenNavigation"
                    : "openNavigation",
                )}
                className="-ms-2 size-8 text-zinc-300 hover:bg-zinc-800 hover:text-white"
              >
                <Menu aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent
              className="w-[314px] max-w-[calc(100vw-1rem)] gap-0 p-0"
              side="left"
              close={false}
            >
              <SheetHeader className="space-y-0 p-0">
                <SheetTitle className="sr-only">{navigationTitle}</SheetTitle>
                <SheetDescription className="sr-only">
                  {t(
                    settings.audience === "supplier"
                      ? "supplierNavigationDescription"
                      : "navigationDescription",
                  )}
                </SheetDescription>
              </SheetHeader>
              <SheetBody className="flex min-w-0 grow p-0">
                <Sidebar onNavigate={() => setIsSheetOpen(false)} />
                <SidebarMenu onNavigate={() => setIsSheetOpen(false)} />
              </SheetBody>
            </SheetContent>
          </Sheet>
        ) : null}
        <Link
          href={
            (settings.audience === "supplier"
              ? "/supplier"
              : "/suppliers") as Route
          }
          className="relative block h-7 w-28 shrink-0 sm:w-32"
          aria-label={
            settings.audience === "supplier"
              ? t("supplierHomeLink")
              : t("homeLink")
          }
        >
          <Image
            src="/wio_veyra_whiteTx.svg"
            alt={t("account.product")}
            fill
            priority
            sizes="128px"
            className="object-contain object-left"
          />
        </Link>
      </div>

      <div className="flex min-w-0 items-center border-s border-zinc-800 ps-2 sm:ps-2.5">
        {settings.audience === "supplier" ? (
          <HeaderTitle />
        ) : (
          <HeaderTitleDefault />
        )}
        {!isMobile ? <HeaderBreadcrumbs /> : null}
      </div>
    </div>
  );
}
